import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classNames from "classnames";

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import CreateIcon from '@material-ui/icons/Create';
import ScheduleIcon from '@material-ui/icons/Schedule';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import useApi from 'app/context/useApi';
import Loading from 'app/components/loaders/Loading';

const usePageStyles = makeStyles(theme => ({
    page: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#ffffff',
        transition: theme.transitions.create('border-color'),
        border: `2px solid ${theme.palette.grey[200]}`,
        borderRadius: theme.shape.borderRadius,
    },
    pageActive: {
        borderColor: theme.palette.primary.main
    },
    pageInactive: {
        borderColor: '#ffffff'
    },
    greenChip: {
        backgroundColor: theme.palette.success.dark,
        color: '#fff',
        marginRight: theme.spacing(3),
        textTransform: 'capitalize'
    },
    yellowChip: {
        backgroundColor: theme.palette.warning.dark,
        color: '#fff',
        marginRight: theme.spacing(3),
        textTransform: 'capitalize'
    },
    greyChip: {
        marginRight: theme.spacing(3),
        textTransform: 'capitalize'
    },
    white: {
        color: '#ffffff'
    },
    marginRight: {
        marginRight: theme.spacing(1)
    },
}));

function Page(props) {

    const { page, onMoveUp, onMoveDown, pageKey, activeKey } = props;
    const classes = usePageStyles();

    const pageClasses = classNames({
        [classes.page]: true,
        [classes.pageActive]: (activeKey === pageKey),
        [classes.pageInactive]: (activeKey !== pageKey)
    });

    const statusChipClass = () => {
        if (page.status === 'published') return classes.greenChip;
        if (page.status === 'scheduled') return classes.yellowChip;
        return classes.greyChip;
    }

    const statusChipIcon = () => {
        if (page.status === 'draft') return <CreateIcon size="small" classes={{ root: classes.white }} />;
        if (page.status === 'scheduled') return <ScheduleIcon size="small" classes={{ root: classes.white }}/>;
        return null
    }

    return (
        <div className={pageClasses}>
            <Typography variant="subtitle1" color={(activeKey === pageKey) ? "primary" : "textSecondary"}>{page.title}</Typography>
            <div>
                <Chip
                    label={page.status}
                    className={statusChipClass()}
                    icon={statusChipIcon()}
                />
                <IconButton
                    aria-label="Move Down"
                    className={classes.marginRight}
                    size="small"
                    onClick={onMoveDown}
                    >
                    <KeyboardArrowDownIcon size="small"/>
                </IconButton>
                <IconButton
                    aria-label="Move Up"
                    size="small"
                    onClick={onMoveUp}
                    >
                    <KeyboardArrowUpIcon size="small"/>
                </IconButton>
            </div>
        </div>
    )
}

const usePageSpacerStyles = makeStyles(theme => ({
    pageSpacer: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: `2px solid ${theme.palette.grey[200]}`,
        transition: theme.transitions.create('border-color'),
        borderRadius: theme.shape.borderRadius,
    },
    pageSpacerActive: {
        borderColor: theme.palette.primary.main
    },
    pageSpacerInactive: {
        borderColor: theme.palette.grey[200]
    },
    marginRight: {
        marginRight: theme.spacing(1)
    },
}));

function PageSpacer(props) {

    const { onMoveUp, onMoveDown, onDelete, activeKey, pageKey } = props;

    const classes = usePageSpacerStyles();

    const spacerClasses = classNames({
        [classes.pageSpacer]: true,
        [classes.pageSpacerActive]: (activeKey === pageKey),
        [classes.pageSpacerInactive]: (activeKey !== pageKey)
    });

    return (
        <div className={spacerClasses}>
            <Typography variant="subtitle2" color={(activeKey === pageKey) ? "primary" : "textSecondary"}>Spacer</Typography>
            <div>
                <IconButton
                    aria-label="Delete Spacer"
                    className={classes.marginRight}
                    size="small"
                    onClick={onDelete}
                    >
                    <DeleteIcon size="small"/>
                </IconButton>
                <IconButton
                    aria-label="Move Down"
                    className={classes.marginRight}
                    size="small"
                    onClick={onMoveDown}
                    >
                    <KeyboardArrowDownIcon size="small"/>
                </IconButton>
                <IconButton
                    aria-label="Move Up"
                    size="small"
                    onClick={onMoveUp}
                    >
                    <KeyboardArrowUpIcon size="small"/>
                </IconButton>
            </div>
        </div>
    )
}

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    pageContainer: {
        backgroundColor: theme.palette.grey[200]
    },
    actions: {
        justifyContent: 'space-between'
    }
}));

const sortAndSpacePages = pages => {
    const sortedPages = pages.sort((a, b) => a.nav_order - b.nav_order);

    const spacedPages = [];

    if (sortedPages.length) {

        for (let i = 0; i <= sortedPages[(sortedPages.length - 1)].nav_order; i++) {
            if (sortedPages[i]) spacedPages.push(sortedPages[i]);
            else spacedPages.push('spacer');
        }
    }

    return spacedPages;
}

export default function PageOrderDialog(props) {

    const classes = useStyles();
    const api = useApi();
    const { open, onClose, initPages, onSave } = props;

    const [ pages, setPages ] = useState([]);
    const [ activePageKey, setActivePageKey ] = useState(null);
    const [ loading, setLoading ] = useState(false);

    // Getters ------------------------------------------------

    const getPages = () => {
        setLoading(true);

        api.get(`/admin/page`).then(res => setPages(sortAndSpacePages(res.pages))).finally(() => setLoading(false))
    };

    useEffect(() => {
        if (initPages) setPages(sortAndSpacePages(initPages));
        else getPages();
    }, [initPages]);

    // Handlers ------------------------------------------------

    const handleNewSpacer = () => {
        const newPages = [...pages];
        const i = newPages.length - 1;

        newPages.splice(i, 0, 'spacer');

        setPages(newPages);
        setActivePageKey(i);
    }

    const handleUp = i => {

        if ((i - 1) === 0 && pages[i] === 'spacer') return;

        const newPages = [...pages];

        const p = newPages.splice(i, 1)[0];

        newPages.splice((i - 1), 0, p);

        setPages(newPages);
        setActivePageKey((i - 1));
    }

    const handleDown = i => {

        if (i === (pages.length - 2) && pages[i] === 'spacer') return;

        const newPages = [...pages];

        const p = newPages.splice(i, 1)[0];

        newPages.splice((i + 1), 0, p);

        setPages(newPages);
        setActivePageKey((i + 1));
    }

    const handleDeleteSpacer = i => {

        if (pages[i] !== 'spacer') return;

        const newPages = [...pages];

        const p = newPages.splice(i, 1)[0];

        setPages(newPages);

        if (i === activePageKey) setActivePageKey(null);
    }

    // Posters ------------------------------------------------

    const postPageOrder = () => {
        setLoading(true);

        api.post('/admin/page/order', { pages: pages }).then(() => onSave()).catch(() => setLoading(false));
    }

    // Constants ------------------------------------------------

    const composedPages = useMemo(() => pages.map((page, i) => {
        if (page !== 'spacer') return (
            <Page
                key={i}
                pageKey={i}
                page={page}
                onMoveUp={() => handleUp(i)}
                onMoveDown={() => handleDown(i)}
                activeKey={activePageKey}
            />
        );
        else return (
            <PageSpacer
                key={i}
                pageKey={i}
                activeKey={activePageKey}
                onMoveUp={() => handleUp(i)}
                onMoveDown={() => handleDown(i)}
                onDelete={() => handleDeleteSpacer(i)}
            />
        )
    }), [pages, activePageKey]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="page-order-title"
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="page-order-title">
                Edit Navigation Page Order
                <IconButton
                    aria-label="Cancel"
                    className={classes.closeButton}
                    onClick={onClose}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.pageContainer}>
                {composedPages}
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button onClick={handleNewSpacer}>
                    Add Spacer
                </Button>
                <Button color='primary' variant="contained" onClick={postPageOrder}>
                    Save
                </Button>
           </DialogActions>
        </Dialog>
    )
}
